<template>
   <div class="pt-[16px] px-[24px] max-w-full">
    <div class="flex justify-between shadow-xl box-border items-center text-white h-[62px] rounded p-4 bg-[#2B2C32] px-8 py-1 mb-2">
      <h2 class="font-bold text-2xl leading-[30px] tracking-[0.05px]">Vault</h2>
      <div class="flex items-center">
        <div class="flex items-center border-r border-[#464851] pr-4">
          <h5 class="font-bold text-xl leading-6 tracking-[0.15px] mr-2">Total In Vault</h5>
          <span class="font-bold text-xl leading-6 tracking-[0.15px] text-[#2BA66B]">${{totalBalance}}</span>
        </div>
        <div class="flex items-center pl-4">
          <h5 class="font-bold text-xl leading-6 tracking-[0.15px] mr-2">Open Date</h5>
          <span class="font-bold text-xl leading-6 tracking-[0.15px] text-[#9E9E9E]">{{openDate}}</span>
        </div>
        <button
            @click="exportVault"
            class="flex flex-row items-center disabled:bg-field-black disabled:text-theme-modalbg text-white bg-theme-green rounded h-10 px-4 uppercase ml-2"
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M19 9H15V3H9V9H5L12 16L19 9ZM11 11V5H13V11H14.17L12 13.17L9.83 11H11ZM19 20V18H5V20H19Z" class="fill-white"/>
            </svg>
            <span class="text-sm">Export Cash Vault</span>
        </button>
      </div>
    </div>
    <ul class="flex bg-[#2B2C32] rounded px-8 py-1 mb-1">
      <li :class="'mr-4 uppercase' + (active == 'current' ? ' active' : '')">
        <router-link :to="{ name: 'CurrentVault' }" href="#" class="inline-block text-theme-white tracking-widest text-sm text-white font-semibold px-2">
          CURRENT VAULT
        </router-link>
      </li>
      <li :class="active == 'transaction' ? ' active' : ''">
        <router-link :to="{ name: 'TransactionHistory' }" class="text-theme-theme-white text-white tracking-widest text-sm font-semibold">
         TRANSACTION HISTORY
        </router-link>
      </li>
    </ul>
  </div>
</template>
<script>
import {defineComponent, ref} from "vue"
import VaultApis from "@/controllers/VaultApis";
import { mapGetters } from "vuex";
import moment from 'moment-timezone'
import writeXlsxFile from 'write-excel-file';
import startCase from 'lodash/startCase'
import lowerCase from 'lodash/lowerCase'
import { useSnackbar } from "vue3-snackbar";

export default defineComponent({
  props: {
    active: {
      required: true,
      type: String,
    },
    callback: {
      required: true,
      type: Function,
    }
  },
  setup() {
    const totalBalance = ref(0)
    const openDate = ref(null)
    const snackbar = useSnackbar()

    return {totalBalance, openDate, snackbar}
  },
  mounted(){
     this.getData()

     this.emitter.on("refresh-vault-balance", data => {
      this.getData();
    });
  },
  beforeDestroy() {
    this.emitter.off('refresh-vault-balance', () => {});
  },
  watch: {
    vg_selectedLocation(){
      this.getData()
    },
  },
  computed: {
    ...mapGetters({
      vg_selectedLocation:  'location/selected'
    })
  },
  methods: {
    async exportVault(){
      try {
        const locationId = this.vg_selectedLocation?.id
        if (!locationId) return

        const dateStr = moment().format('YYYY-MM-DD')
        const [currentRes, historyRes] = await Promise.all([
          VaultApis.list(locationId, 'noPage=true&closed=false'),
          VaultApis.list(locationId, 'noPage=true&closed=true'),
        ])

        const currentRows = currentRes.status === 200
          ? (currentRes.data.payload.data || currentRes.data.payload || [])
          : []
        const historyRows = historyRes.status === 200
          ? (historyRes.data.payload.data || historyRes.data.payload || [])
          : []

        await this.downloadVaultExcel(currentRows, historyRows, `vault-export-${dateStr}.xlsx`)

        this.snackbar.add({
          type: 'info',
          text: 'File downloaded',
          duration: 3000,
        })
      } catch (err) {
        console.log(err)
        this.snackbar.add({
          type: 'info',
          text: 'Something went wrong. Please try again later.',
          duration: 3000,
        })
      }
    },
    getVaultExportSchema() {
      return [
        {
          column: 'Amount',
          type: String,
          value: row => this.getExportAmount(row),
        },
        {
          column: 'Transaction Type',
          type: String,
          value: row => this.ucWordTransactionType(row.transactionType),
        },
        {
          column: 'Date',
          type: String,
          value: row => this.formatExportDate(row.createdAt),
        },
        {
          column: 'User',
          type: String,
          value: row => row.createdByName ? startCase(lowerCase(row.createdByName)) : '',
        },
        {
          column: 'Notes',
          type: String,
          value: row => row.note || '',
        },
        {
          column: 'Bank Deposit #',
          type: String,
          value: row => row.depositId || '',
        },
      ]
    },
    async downloadVaultExcel(currentRows, historyRows, fileName) {
      const schema = this.getVaultExportSchema()

      const xlBlob = await writeXlsxFile([currentRows, historyRows], {
        schema: [schema, schema],
        sheets: ['Current Vault', 'Transaction History'],
      })
      const url = URL.createObjectURL(xlBlob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', fileName)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    getExportAmount(item) {
      if (item.transactionType === 'remove cash' || item.transactionType === 'bank deposit') {
        return item.amount ? '-' + Math.abs(item.amount).toFixed(2) : '0.00'
      }
      return item.amount ? Math.abs(item.amount).toFixed(2) : '0.00'
    },
    ucWordTransactionType(words) {
      if (!words) return ''
      if (words === 'safe audit') return 'Safe Audit'
      if (words === 'close vault') return 'Close Vault'
      return startCase(lowerCase(words))
    },
    formatExportDate(date) {
      if (!date) return ''
      const defaultTimeZone = this.vg_selectedLocation.timezone || 'America/Toronto'
      return moment(date).tz(defaultTimeZone).format('YYYY-MM-DD')
    },
    formatCurrency(val){
      if(!val) return 0

      return val.toFixed(2)
    },
    formatDate(date){
      if(!date) return

      return moment(date).format('YYYY-MM-DD')
    },
    async getData(){
      let res = await VaultApis.getVaultBalance(this.vg_selectedLocation.id)
      if(res.status===200){
        this.totalBalance = this.formatCurrency(res.data.payload.vaultBalance)
        this.openDate = this.formatDate(res.data.payload.openDate)
        this.$emit('updateBalance', this.totalBalance)
      }
    }
  }
})
</script>

<style scoped>
  .active:before {
    content: '';
    position: absolute;
    background: #2BA66B;
    width: 100%;
    height: 4px;
    bottom: -8px;
  }
  .active {
    position:relative;
  }
</style>